import { useModuleBEM } from "@b2bportal/core-themes";
import type { HotelItinerary } from "@b2bportal/lodging-api";
import { useI18nContext, Trans } from "@hopper-b2b/i18n";
import clsx from "clsx";
import { useHotelReservationPayment } from "../../hooks";
import styles from "./styles.module.scss";
import { defaultPointsExchange } from "@/utils/points";

export const HotelReservationPagePaymentDetails = ({
  itinerary,
}: {
  itinerary: HotelItinerary;
}) => {
  const { t, formatFiatCurrency } = useI18nContext();
  const { breakdown } = useHotelReservationPayment({
    itinerary,
  });
  const [_block, cn] = useModuleBEM(styles, "PaymentDetails");

  // Separate pay-now taxes (rateTaxesAndFees) from pay-later taxes
  const payNowTaxes = breakdown.payNowTaxesAndFees;

  const payLaterTaxes = breakdown.payLaterTaxesAndFees;

  const hasPayLaterTaxes = payLaterTaxes.length > 0;

  return (
    <div className={cn("container")}>
      {/* Price breakdown items (pay now) */}
      <div className={cn("price-items")}>
        <div className={cn("line-item")}>
          <div className={cn("label")}>{breakdown.baseAmount.label}</div>
          <div className={cn("value")}>{breakdown.baseAmount.value}</div>
        </div>
        {payNowTaxes.map((fee, index) => (
          <div key={index} className={cn("line-item")}>
            <div className={cn("label-with-adornment")}>
              <span className={cn("label")}>{fee.label}</span>
              {fee.labelEndAdornment}
            </div>
            <div className={cn("value")}>{fee.value}</div>
          </div>
        ))}
      </div>

      <div className={cn("divider")} />

      {/* Pay at property section */}
      {hasPayLaterTaxes && (
        <>
          <div className={cn("section")}>
            <div className={cn("section-title")}>
              {t("hotels.priceBreakdown.payAtProperty")}
            </div>
            {payLaterTaxes.map((fee, index) => (
              <div key={index} className={cn("line-item")}>
                <div className={cn("label")}>{fee.label}</div>
                <div className={cn("value")}>{fee.value}</div>
              </div>
            ))}
          </div>
          <div className={cn("divider")} />
        </>
      )}

      {/* Points applied */}
      {breakdown.summaryPoints && breakdown.summaryPoints.value > 0 && (
        <>
          <div className={cn("line-item")}>
            <div className={cn("label")}>{breakdown.summaryPoints.label}</div>
            <div className={clsx(cn("value"), cn("points-value"))}>
              -
              {formatFiatCurrency(
                defaultPointsExchange.pointsToFiat(
                  breakdown.summaryPoints.value
                )
              )}
            </div>
          </div>
          <div className={cn("divider")} />
        </>
      )}

      {/* Trip Total */}
      <div className={clsx(cn("line-item"), cn("trip-total"))}>
        <div className={clsx(cn("label"), cn("trip-total-label"))}>
          {breakdown.tripTotal.label}
        </div>
        <div className={clsx(cn("value"), cn("trip-total-value"))}>
          {breakdown.tripTotal.value}
        </div>
      </div>

      {/* Payment breakdown below trip total */}
      <div className={cn("section")}>
        {/* Card payment */}
        {breakdown.summaryCharged && (
          <div className={cn("line-item")}>
            <div className={cn("label")}>{breakdown.summaryCharged.label}</div>
            <div className={clsx(cn("value"), cn("bold-value"))}>
              {breakdown.summaryCharged.value}
            </div>
          </div>
        )}

        {/* Due at property */}
        {hasPayLaterTaxes && payLaterTaxes.length > 0 && (
          <div className={cn("line-item")}>
            <div className={cn("label")}>
              {t("hotels.priceBreakdown.dueAtProperty")}
            </div>
            <div className={clsx(cn("value"), cn("bold-value"))}>
              {breakdown.payAtPropertyTotal.value}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
